/**
 * The simulation facade: composes the sim subsystems, owns the engine, and
 * routes committed commands to whichever subsystem handles them.
 *
 * Everything outside `src/sim/` talks to the simulation through this object
 * and through the relay. There is no other write path (R4).
 */

import { Registry, type Subsystem } from '../core/registry.js'
import { Engine } from '../core/engine.js'
import { HashRegistry } from '../core/hash.js'
import { Rng } from '../core/rng.js'
import { type Config, defaultConfig, HASH_INTERVAL, MAX_PLAYERS } from '../core/config.js'
import type { Command, CommandWorld } from '../net/commands.js'
import { LocalRelay } from '../net/local.js'
import type { Relay } from '../net/relay.js'
import { World } from './world.js'
import { Entities, EF } from './entities.js'
import * as fix from '../core/fixed.js'
import { generateMap } from './mapgen.js'
import { Pathfind } from './pathfind.js'
import { Vision } from './vision.js'
import { Economy } from './economy.js'
import { Build } from './build.js'
import { Combat } from './combat.js'
import { SkirmishAI } from './ai.js'
import { SimBridge } from './bridge.js'
import { factionById } from './data/factions.js'
import { defById, founderUnit, type EntityDef } from './data/entities.js'

/**
 * A subsystem that consumes commands. Offered each command in registry order;
 * the first to return true owns it. Registry order is deterministic, so this
 * dispatch is too.
 */
export interface CommandHandler extends Subsystem {
  handleCommand(cmd: Command, tick: number): boolean
}

function handlesCommands(s: Subsystem): s is CommandHandler {
  return typeof (s as CommandHandler).handleCommand === 'function'
}

export interface PlayerSlot {
  /** Faction id, indexing `src/sim/data/factions.ts`. */
  faction: number
  /** Team id; slots sharing one do not fight each other. */
  team: number
  /** 'human' | 'ai' | 'open' — presentation only, never simulation state. */
  kind?: string
  /**
   * How hard this seat plays, if it is an AI. 0 easy, 1 normal, 2 hard.
   *
   * Part of the **match definition**, not a client preference, because it
   * changes what the AI does and therefore what the simulation computes. A
   * difficulty picked locally would desync the moment two players chose
   * differently.
   */
  difficulty?: number
}

export interface MatchDefinition {
  mapSeed: number | string
  slots: PlayerSlot[]
  /** Simulation RNG seed. Defaults to the map seed. */
  seed?: number | string
  /**
   * Everything finishes the tick after it is queued. For trying things out
   * without waiting through a build order.
   *
   * A property of the *match*, not of the client, and deliberately so: it
   * changes simulation timing, so a client that flipped it locally would desync
   * within a tick. Both sides agree on it before the match starts or neither
   * has it.
   */
  instantBuild?: boolean
}

export interface SimOptions {
  match: MatchDefinition
  /** Slot this client controls. -1 for a pure observer or a bot-match host. */
  localSlot?: number
  relay?: (world: CommandWorld) => Relay
  config?: Config
  hashInterval?: number
  /**
   * Register presentation subsystems before `init()` runs. Attaching after
   * initialization is rejected, because a subsystem that missed the
   * dependency-ordered `init` pass has no `ctx`.
   */
  attach?: (registry: Registry) => void
}

export const MATCH = {
  Running: 0,
  Won: 1,
  Stalled: 2,
} as const

export class Sim {
  readonly registry: Registry
  readonly world: World
  readonly entities: Entities
  readonly engine: Engine<Command>
  readonly relay: Relay
  readonly hash: HashRegistry
  readonly rng: Rng
  readonly match: MatchDefinition

  /** Match outcome, part of the state hash so a divergent result is caught. */
  readonly outcome = new Int32Array(3) // [status, winner, endTick]

  private handlers: CommandHandler[] = []

  private constructor(opts: SimOptions) {
    this.match = opts.match
    const seed = opts.match.seed ?? opts.match.mapSeed
    this.rng = new Rng(seed)
    this.hash = new HashRegistry()

    this.registry = new Registry({
      rng: this.rng,
      hash: this.hash,
      config: opts.config ?? defaultConfig(),
    })

    this.world = new World()
    this.entities = new Entities()
    // The map is generated **before** `registry.init()` runs. Subsystems build
    // derived structures from the world during init — the renderer's terrain
    // geometry, the pathfinder's cluster graph — and generating afterwards
    // leaves every one of them describing an empty map.
    generateMap(this.world, {
      seed: this.match.mapSeed,
      playerCount: Math.min(this.match.slots.length, MAX_PLAYERS),
    })
    this.registry.add(World, this.world)
    this.registry.add(Entities, this.entities)

    // The relay is constructed against a world view that only exposes what
    // validation needs, so `src/net/` never learns the entity layout.
    const commandWorld: CommandWorld = {
      isLive: (id) => this.entities.isLive(id),
      ownerOf: (id) => this.entities.ownerOf(id),
      isStructure: (id) => {
        const slot = this.entities.resolve(id)
        return slot >= 0 && this.entities.has(slot, EF.Structure)
      },
    }
    this.relay =
      opts.relay?.(commandWorld) ??
      new LocalRelay({ localSlot: opts.localSlot ?? 0, world: commandWorld })

    this.engine = new Engine<Command>({
      registry: this.registry,
      source: this.relay,
      applyCommands: (tick, cmds) => this.applyCommands(tick, cmds),
      hash: this.hash,
      rng: this.rng,
      hashInterval: opts.hashInterval ?? HASH_INTERVAL,
    })
  }

  /**
   * Build a simulation from a match definition. Starting state derives from
   * `{mapSeed, slots}` alone (R32), so two clients given the same definition
   * begin from an identical hash.
   */
  static create(opts: SimOptions): Sim {
    const sim = new Sim(opts)
    sim.registerSubsystems()
    opts.attach?.(sim.registry)
    sim.registry.init()
    // Registered after the subsystems so the match outcome is inside the hash.
    sim.hash.register('sim:outcome', sim.outcome)
    sim.handlers = sim.registry.order
      .map((id) => sim.registry.get<Subsystem>(id))
      .filter(handlesCommands)
    sim.setupMatch()

    // The AI submits through the same relay as the human. It is a client of
    // the command API, not a privileged mutator.
    const ai = sim.registry.get<SkirmishAI>('ai')
    ai.relay = sim.relay
    for (let slot = 0; slot < sim.match.slots.length; slot++) {
      const seat = sim.match.slots[slot]
      if (seat.kind === 'ai') ai.control(slot, seat.difficulty)
    }
    return sim
  }

  /**
   * Hook for the composition root to add gameplay subsystems. Kept as a
   * method so tests can build a minimal simulation without pathfinding,
   * economy, or combat present.
   */
  protected registerSubsystems(): void {
    this.registry.add(
      SimBridge,
      new SimBridge(
        (kind, owner, cx, cy) => this.spawnDef(kind, owner, cx, cy),
        (status, winner) => this.finish(status, winner),
        this.match.instantBuild ?? false,
      ),
    )
    this.registry.add(Pathfind, new Pathfind())
    this.registry.add(Vision, new Vision(Math.max(2, this.match.slots.length)))
    this.registry.add(Economy, new Economy())
    this.registry.add(Build, new Build())
    this.registry.add(Combat, new Combat())
    this.registry.add(SkirmishAI, new SkirmishAI())
    for (const factory of Sim.extensions) factory(this.registry)
  }

  /**
   * Spawn from a registry definition, deriving flags, hit points, and footprint
   * from data. Nothing else in the codebase should build a `SpawnParams` by
   * hand — every entity's shape is a data property (KTD5).
   */
  spawnDef(kind: number, owner: number, cx: number, cy: number): number {
    const def = defById(kind)
    if (!def) return 0
    let flags = 0
    if (def.isStructure) flags |= EF.Structure
    else flags |= EF.Mobile
    if (def.founderOf !== undefined) flags |= EF.Founder
    if (def.isHarvester) flags |= EF.Harvester
    if (def.isAir) flags |= EF.Air

    const id = this.entities.spawn({
      kind,
      owner,
      x: this.world.cellCenterX(cx),
      y: this.world.cellCenterY(cy),
      hp: def.hp,
      flags,
      ammo: def.ordnance ?? 0,
    })
    if (id === 0) return 0

    if (def.isStructure) {
      const slot = this.entities.resolve(id)
      if (!this.world.occupyFootprint(cx, cy, def.width, def.height, id)) {
        this.entities.destroy(id)
        return 0
      }
      // A structure's anchor is its top-left cell, but it draws and is targeted
      // from its centre.
      this.entities.x[slot] = fix.add(
        this.world.cellCenterX(cx),
        fix.fromFloat((def.width - 1) / 2),
      )
      this.entities.y[slot] = fix.add(
        this.world.cellCenterY(cy),
        fix.fromFloat((def.height - 1) / 2),
      )
      this.registry.emit('world:passability', { cx, cy })
      this.registry.emit('struct:placed', { id, kind, owner, x: cx, y: cy })
    }
    return id
  }

  /** Definition backing a live entity slot, or undefined. */
  defOf(slot: number): EntityDef | undefined {
    return defById(this.entities.kind[slot])
  }

  /**
   * Subsystem factories added by later units. Each receives the registry and
   * adds itself. Order does not matter — the registry sorts by `deps`.
   */
  static readonly extensions: Array<(registry: Registry) => void> = []

  /**
   * Starting entities and per-player state (R32).
   *
   * Everything here derives from `{mapSeed, slots}` alone, so two clients given
   * the same match definition begin from an identical state hash. The map
   * already exists by this point.
   */
  private setupMatch(): void {
    const slots = this.match.slots.slice(0, MAX_PLAYERS)
    const economy = this.registry.get<Economy>('economy')
    for (let slot = 0; slot < slots.length; slot++) {
      const faction = slots[slot].faction
      economy.addCredits(slot, factionById(faction)?.startingCredits ?? 5000)
      const sx = this.world.starts[slot * 2]
      const sy = this.world.starts[slot * 2 + 1]
      if (sx < 0) continue

      // The founder, and nothing else. F1's opening move is deploying it into
      // a base, exactly as Red Alert opens.
      //
      // An earlier version also spawned a small escort so that U21's first
      // playable had a group to move. Once the build system landed that escort
      // became actively harmful: its value alone exceeded the AI's attack
      // threshold, so every bot match opened with a starting-unit rush and
      // resolved in three minutes without either side reaching a real army.
      const founder = founderUnit(faction)
      this.spawnDef(founder.id, slot, sx, sy)
    }
    this.registry.emit('match:setup', { slots: slots.length })
  }

  private applyCommands(tick: number, commands: readonly Command[]): void {
    for (let i = 0; i < commands.length; i++) {
      const cmd = commands[i]
      let handled = false
      for (let h = 0; h < this.handlers.length && !handled; h++) {
        handled = this.handlers[h].handleCommand(cmd, tick)
      }
      if (!handled) {
        // A command with no owner is a wiring bug, not a player error, and it
        // must be loud: silently dropping it on one client and handling it on
        // another is precisely a desync.
        this.registry.emit('order:rejected', { owner: cmd.slot, reason: 'no-producer' })
      }
    }
  }

  get tick(): number {
    return this.engine.tick
  }

  get status(): number {
    return this.outcome[0]
  }

  get winner(): number {
    return this.outcome[1]
  }

  /** Declare the match over. Idempotent — the first result stands. */
  finish(status: number, winner: number): void {
    if (this.outcome[0] !== MATCH.Running) return
    this.outcome[0] = status
    this.outcome[1] = winner
    this.outcome[2] = this.engine.tick
    this.registry.emit('match:over', { winner, reason: status === MATCH.Won ? 'win' : 'stall' })
  }

  step(): boolean {
    return this.engine.step()
  }

  runTo(tick: number): number {
    return this.engine.runTo(tick)
  }

  currentHash(): number {
    return this.hash.compute()
  }

  dispose(): void {
    this.relay.dispose()
    this.registry.dispose()
  }
}
